import { computed, observable } from 'mobx'

import { OrderELockInfoDTO } from 'sections/LKSection/modules/ELockModule/types'
import {
  ConfirmedToOpen,
  CouriersAccess,
  OrderDTO,
  OrderStatus,
  PickPointLocation,
  SignboardFormat,
  SignboardSize,
  SubsidyHexType,
} from 'sections/LKSection/modules/OrderModule/types'
import { parseTariffDescription } from 'sections/LKSection/modules/OrderModule/utils'
import { PickPointFileItemType, PickPointType, PickPointVideoItemType } from 'sections/LKSection/shared/PickPointSharedModule/types'
import { getMedia } from 'sections/LKSection/shared/PickPointSharedModule/utils'
import { Entity } from 'shared/stores/BaseStore'
import { Country } from 'shared/types/types'

import { OrderComment } from './OrderComment/OrderComment'

export class Order extends Entity {
  @observable cityId: number = 0
  @observable cityName: string = ''
  @observable country: Country = Country.RU
  @observable description: string = ''
  @observable courierWay: string = ''
  @observable district: string = ''
  @observable house: string = ''
  @observable region: string = ''
  @observable latitude: string = ''
  @observable longitude: string = ''
  @observable street: string = ''
  @observable typePoint: PickPointType = PickPointType.none
  @observable confirmedToOpen: ConfirmedToOpen = ConfirmedToOpen.none
  @observable subsidyHexType: SubsidyHexType = SubsidyHexType.none
  @observable couriersAccess: CouriersAccess = CouriersAccess.none

  @observable dressingRoomsCount: number = 0
  @observable floorLevel: PickPointLocation = PickPointLocation.firstFloor

  @observable updated: string = ''
  @observable created: string = ''
  @observable deleted?: string | null
  @observable accepted?: string | null
  @observable status: OrderStatus = OrderStatus.draft
  @observable tariffDescription: string[] = []
  @observable outOfDraftDate?: string | null
  @observable comments: OrderComment[] = []
  @observable timetable = ''

  @observable area: number = 0
  @observable warehouseArea: number = 0
  @observable cellingHeight: number = 0

  @observable photos: PickPointFileItemType[] = []
  @observable videos: PickPointVideoItemType[] = []
  @observable documents: PickPointFileItemType[] = []

  @observable isBranded: boolean = false
  /** признак фирменного ПВЗ */
  @observable wbCompany: boolean = false
  @observable pickpointId?: number
  /** признак переезда */
  @observable exPickPointId = 0

  @observable signboardSize: SignboardSize = SignboardSize.none
  @observable signboardFormat: SignboardFormat = SignboardFormat.none

  @observable ownerGln = ''
  @observable glnPvz = ''
  @observable molName = ''

  @observable eLockInfo: OrderELockInfoDTO | null = null

  /* Здесь перечислены свойства, которых нет в DTO модели */
  constructor(props: Omit<Order, 'fullAddress'>) {
    super(props)
    Object.assign(this, props)
  }

  static create({ actualUrls, comments, tariffDescription, stockArea, ...other }: OrderDTO) {
    const { videos, photos, documents } = getMedia(actualUrls)

    const tariffDescriptionArray = tariffDescription ? parseTariffDescription(tariffDescription) : []

    return new Order({
      ...other,
      photos,
      videos,
      documents,
      comments: comments?.map((comment) => OrderComment.create(comment)) ?? [],
      tariffDescription: tariffDescriptionArray,
      warehouseArea: stockArea,
      exPickPointId: other?.exPickpointId,
      eLockInfo: other?.elockInfo,
      couriersAccess: other?.accessType,
      floorLevel: other?.locations?.[0] ?? PickPointLocation.none,
      glnPvz: other.pickpointGln,
    })
  }

  @computed
  get fullAddress() {
    if (!this.cityName) {
      return ''
    }
    if (!this.street) {
      return this.cityName
    }
    if (!this.house) {
      return `${this.cityName}, ${this.street}`
    }
    if (!this.district || this.district.trim?.().length === 0) {
      return `${this.cityName}, ${this.street}, ${this.house}`
    }

    return `${this.district}, ${this.cityName}, ${this.street}, ${this.house}`
  }
}
